import React, {useState, useEffect} from "react";
import secureStorage from "../../utils/secureStorage";
import {api} from "../../config/api";
import Notification from "../Common/Notification";
import usePermissions from "../../hooks/usePermissions";
import {SettingsForm, FormSection, SwitchRow, SaveAction, ListLoader} from "./SettingsUI";

/**
 * Réglages > Caisse > Ticket de clôture. Option de compte (`Account`) lue par
 * le POS via getCatalogue : à la clôture du journal, imprime le stock restant
 * des produits stockables vendus dans ce journal (en ligne uniquement — sans
 * internet la section n'est pas imprimée).
 */
const ClosingTicketSettings = () => {
    const {canUpdate} = usePermissions();
    const canManage = canUpdate("settings");
    const [notification, setNotification] = useState({show: false, type: "success", message: ""});
    const [accountInfo, setAccountInfo] = useState(null);
    const [showStock, setShowStock] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        setTimeout(() => setNotification({show: false, type: "", message: ""}), 3000);
    };

    useEffect(() => {
        const load = async () => {
            try {
                const account = await secureStorage.getItem("selectedAccount");
                if (!account) return;
                setAccountInfo(account);
                setShowStock(!!account.closingTicketShowStock);
                // The cached account predates this option — read the live value.
                const response = await api.get(`/accounts/${account.id}`, {
                    headers: {"X-account-id": account.id, Accept: "application/json"},
                });
                setShowStock(!!response.data?.closingTicketShowStock);
            } catch (error) {
                console.error("Erreur lors du chargement du compte:", error);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const handleSave = async () => {
        if (!accountInfo) return;
        try {
            setIsSaving(true);
            const response = await api.put(`/accounts/${accountInfo.id}`, {closingTicketShowStock: showStock}, {
                headers: {
                    "X-account-id": accountInfo.id,
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });
            const updatedAccount = {...accountInfo, ...response.data};
            await secureStorage.setItem("selectedAccount", updatedAccount);
            setAccountInfo(updatedAccount);
            showNotification("success", "Réglage enregistré. Il sera appliqué par les caisses à leur prochaine synchronisation.");
        } catch (error) {
            console.error("Error updating closing ticket settings:", error);
            showNotification("error",
                error.response?.data?.detail || error.response?.data?.message || "Une erreur est survenue lors de la mise à jour");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <ListLoader label="Chargement des réglages..."/>;

    return (
        <SettingsForm>
            {notification.show && (
                <Notification type={notification.type} message={notification.message} show={notification.show}/>
            )}

            <SaveAction onClick={handleSave} saving={isSaving} disabled={!accountInfo || !canManage}/>

            <FormSection
                title="Ticket de clôture du journal"
                description="Informations ajoutées au ticket imprimé par la caisse à la fermeture du journal."
            >
                <SwitchRow
                    id="closingTicketShowStock"
                    label="Afficher le stock restant des produits vendus"
                    description="Quantité restante de chaque produit stockable vendu dans le journal. Nécessite internet au moment de la clôture : hors ligne, cette section n'est pas imprimée."
                    checked={showStock}
                    disabled={!canManage}
                    onChange={setShowStock}
                />
            </FormSection>
        </SettingsForm>
    );
};

export default ClosingTicketSettings;
